import React from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Typography,
  Alert,
  Snackbar
} from '@mui/material';
import apiClient from '../../services/api';

const PersonalInfoForm = ({ initialData = {}, onSave, onCancel, loading = false }) => {
  const [formData, setFormData] = React.useState({
    personalInfo: { ...initialData.personalInfo },
    englishLanguage: { ...initialData.englishLanguage }
  });
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = React.useState(false);
  const [isDataModified, setIsDataModified] = React.useState(false);

  // 监听initialData变化，更新表单数据
  React.useEffect(() => {
    // 只有在数据没有被修改的情况下才更新表单数据
    if (!isDataModified) {
      setFormData({
        personalInfo: { ...initialData.personalInfo },
        englishLanguage: { ...initialData.englishLanguage }
      });
    }
  }, [initialData, isDataModified]);

  // 监听loading状态，当外部触发保存时自动执行保存逻辑
  React.useEffect(() => {
    if (loading) {
      handleSave();
    }
  }, [loading]);

  // 处理表单字段变化
  const handleFormChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // 标记数据已被修改
    setIsDataModified(true);
    
    // 清除对应字段的错误
    if (errors[`${section}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};
    const { personalInfo, englishLanguage } = formData;
    
    // 验证个人基本信息
    if (!personalInfo?.fullName) newErrors['personalInfo.fullName'] = '请输入姓名';
    if (!personalInfo?.gender) newErrors['personalInfo.gender'] = '请选择性别';
    if (!personalInfo?.dateOfBirth) newErrors['personalInfo.dateOfBirth'] = '请输入出生日期';
    if (!personalInfo?.nationality) newErrors['personalInfo.nationality'] = '请输入国籍';
    if (!personalInfo?.passportNumber) newErrors['personalInfo.passportNumber'] = '请输入护照号码';
    if (!personalInfo?.phone) newErrors['personalInfo.phone'] = '请输入手机号码';
    if (!personalInfo?.email) newErrors['personalInfo.email'] = '请输入邮箱地址';
    
    // 验证英语语言信息
    if (!englishLanguage?.languageType) newErrors['englishLanguage.languageType'] = '请选择英语语言能力';
    if (englishLanguage?.languageType === 'OTHERS' && !englishLanguage?.languageCustomType) {
      newErrors['englishLanguage.languageCustomType'] = '请填写英语语言能力';
    }
    if (englishLanguage?.languageType && englishLanguage?.languageType !== 'NOT' && !englishLanguage?.languageResult) {
      newErrors['englishLanguage.languageResult'] = '请填写英语语言成绩';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理保存
  const handleSave = async () => {
    setSaveError('');
    
    if (!validateForm()) {
      // 验证失败时，显示错误提示
      setShowErrorSnackbar(true);
      // 通知父组件保存失败
      onSave && onSave(null);
      return;
    }

    try {
      const response = await apiClient.post('/api/application/update-personal-info', formData);
      
      if (response && response.success) {
        // 保存成功后，重置修改标记
        setIsDataModified(false);
        
        // 解析返回的数据
        let updatedData = formData;
        if (response.data) {
          try {
            const parsedData = JSON.parse(response.data);
            updatedData = {
              personalInfo: parsedData,
              englishLanguage: formData.englishLanguage
            };
            // 更新本地表单数据，保持用户修改后的数据
            setFormData(updatedData);
          } catch (parseError) {
            console.error('解析返回数据失败:', parseError);
          }
        }
        onSave && onSave(updatedData);
      } else {
        setSaveError(response?.message || '保存失败');
        setShowErrorSnackbar(true);
        onSave && onSave(null);
      }
    } catch (error) {
      console.error('保存个人信息失败:', error);
      setSaveError(error?.response?.data?.message || error.message || '保存失败');
      setShowErrorSnackbar(true);
      onSave && onSave(null);
    }
  };

  // 关闭错误提示
  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
  };

  const { personalInfo = {}, englishLanguage = {} } = formData;

  return (
    <Box>
      {/* 移除固定位置的错误提示 */}
      
      {/* 个人基本信息 */}
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        个人基本信息
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="姓名 *"
            value={personalInfo.fullName || ''}
            onChange={(e) => handleFormChange('personalInfo', 'fullName', e.target.value)}
            error={!!errors['personalInfo.fullName']}
            helperText={errors['personalInfo.fullName']}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors['personalInfo.gender']}>
            <InputLabel>性别 *</InputLabel>
            <Select
              value={personalInfo.gender || ''}
              onChange={(e) => handleFormChange('personalInfo', 'gender', e.target.value)}
              label="性别 *"
            >
              <MenuItem value="Male">男</MenuItem>
              <MenuItem value="Female">女</MenuItem>
            </Select>
            {errors['personalInfo.gender'] && (
              <FormHelperText>{errors['personalInfo.gender']}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="护照号码 *"
            value={personalInfo.passportNumber || ''}
            onChange={(e) => handleFormChange('personalInfo', 'passportNumber', e.target.value)}
            error={!!errors['personalInfo.passportNumber']}
            helperText={errors['personalInfo.passportNumber']}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="出生日期 *"
            type="date"
            value={personalInfo.dateOfBirth || ''}
            onChange={(e) => handleFormChange('personalInfo', 'dateOfBirth', e.target.value)}
            error={!!errors['personalInfo.dateOfBirth']}
            helperText={errors['personalInfo.dateOfBirth']}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="国籍 *"
            value={personalInfo.nationality || ''}
            onChange={(e) => handleFormChange('personalInfo', 'nationality', e.target.value)}
            error={!!errors['personalInfo.nationality']}
            helperText={errors['personalInfo.nationality']}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="手机号码 *"
            value={personalInfo.phone || ''}
            onChange={(e) => handleFormChange('personalInfo', 'phone', e.target.value)}
            error={!!errors['personalInfo.phone']}
            helperText={errors['personalInfo.phone']}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="邮箱地址 *"
            type="email"
            value={personalInfo.email || ''}
            onChange={(e) => handleFormChange('personalInfo', 'email', e.target.value)}
            error={!!errors['personalInfo.email']}
            helperText={errors['personalInfo.email']}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="身份证号码"
            value={personalInfo.idCardNumber || ''}
            onChange={(e) => handleFormChange('personalInfo', 'idCardNumber', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>宗教信仰</InputLabel>
            <Select
              value={personalInfo.religion || ''}
              onChange={(e) => handleFormChange('personalInfo', 'religion', e.target.value)}
              label="宗教信仰"
            >
              <MenuItem value="NO RELIGION">无宗教信仰</MenuItem>
              <MenuItem value="TAO">道教</MenuItem>
              <MenuItem value="CONFUCIANISM">儒教</MenuItem>
              <MenuItem value="BUDDHIST">佛教</MenuItem>
              <MenuItem value="BAHAI">巴哈伊教</MenuItem>
              <MenuItem value="JUDAISM">犹太教</MenuItem>
              <MenuItem value="HINDU">印度教</MenuItem>
              <MenuItem value="ISLAM">伊斯兰教</MenuItem>
              <MenuItem value="CHRISTIAN">基督教</MenuItem>
              <MenuItem value="SIKHISM">锡克教</MenuItem>
              <MenuItem value="OTHERS">其他</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>婚姻状况</InputLabel>
            <Select
              value={personalInfo.maritalStatus || ''}
              onChange={(e) => handleFormChange('personalInfo', 'maritalStatus', e.target.value)}
              label="婚姻状况"
            >
              <MenuItem value="Single">单身</MenuItem>
              <MenuItem value="Married">已婚</MenuItem>
              <MenuItem value="Widower">鳏夫</MenuItem>
              <MenuItem value="Widow">寡妇</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="date"
            fullWidth
            label="护照签发日期"
            value={personalInfo.passportIssueDate || ''}
            onChange={(e) => handleFormChange('personalInfo', 'passportIssueDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            type="date"
            fullWidth
            label="护照到期日期"
            value={personalInfo.passportExpiryDate || ''}
            onChange={(e) => handleFormChange('personalInfo', 'passportExpiryDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="护照签发地点"
            value={personalInfo.passportIssuePlace || ''}
            onChange={(e) => handleFormChange('personalInfo', 'passportIssuePlace', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="身高(CM)"
            value={personalInfo.bodyHeight || ''}
            onChange={(e) => handleFormChange('personalInfo', 'bodyHeight', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="体重(KG)"
            value={personalInfo.bodyWeight || ''}
            onChange={(e) => handleFormChange('personalInfo', 'bodyWeight', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="月薪"
            value={personalInfo.salary || ''}
            onChange={(e) => handleFormChange('personalInfo', 'salary', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="父亲姓名"
            value={personalInfo.fatherName || ''}
            onChange={(e) => handleFormChange('personalInfo', 'fatherName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="母亲姓名"
            value={personalInfo.motherName || ''}
            onChange={(e) => handleFormChange('personalInfo', 'motherName', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>是否参加高考</InputLabel>
            <Select
              value={personalInfo.ifCee || ''}
              onChange={(e) => handleFormChange('personalInfo', 'ifCee', e.target.value)}
              label="是否参加高考"
            >
              <MenuItem value="1">是</MenuItem>
              <MenuItem value="0">否</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="兄弟姐妹数量"
            value={personalInfo.siblingsCount || ''}
            onChange={(e) => handleFormChange('personalInfo', 'siblingsCount', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="您在兄弟姐妹中的排行"
            value={personalInfo.siblingsRanking || ''}
            onChange={(e) => handleFormChange('personalInfo', 'siblingsRanking', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="通讯地址"
            multiline
            rows={2}
            value={personalInfo.CorrespondenceAddress || ''}
            onChange={(e) => handleFormChange('personalInfo', 'CorrespondenceAddress', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="永久居住地址"
            multiline
            rows={2}
            value={personalInfo.PermanentAddress || ''}
            onChange={(e) => handleFormChange('personalInfo', 'PermanentAddress', e.target.value)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* 英语语言信息 */}
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        英语语言信息
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors['englishLanguage.languageType']}>
            <InputLabel>英语语言能力 *</InputLabel>
            <Select
              value={englishLanguage.languageType || ''}
              onChange={(e) => handleFormChange('englishLanguage', 'languageType', e.target.value)}
              label="英语语言能力 *"
            >
              <MenuItem value="IELTS">雅思</MenuItem>
              <MenuItem value="TOEFL">托福</MenuItem>
              <MenuItem value="Cambridge English Qualifications">剑桥英语</MenuItem>
              <MenuItem value="PTE">PTE</MenuItem>
              <MenuItem value="Linguaskill">领思</MenuItem>
              <MenuItem value="MUET">MUET</MenuItem>
              <MenuItem value="HEET">HEET</MenuItem>
              <MenuItem value="Duolingo English Test">多邻国</MenuItem>
              <MenuItem value="NOT">暂未取得</MenuItem>
              <MenuItem value="OTHERS">其他</MenuItem>
            </Select>
            {errors['englishLanguage.languageType'] && (
              <FormHelperText>{errors['englishLanguage.languageType']}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        {englishLanguage.languageType === 'OTHERS' && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="其他英语语言能力"
              value={englishLanguage.languageCustomType || ''}
              onChange={(e) => handleFormChange('englishLanguage', 'languageCustomType', e.target.value)}
              error={!!errors['englishLanguage.languageCustomType']}
              helperText={errors['englishLanguage.languageCustomType']}
            />
          </Grid>
        )}
        {englishLanguage.languageType && englishLanguage.languageType !== 'NOT' && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="成绩"
              value={englishLanguage.languageResult || ''}
              onChange={(e) => handleFormChange('englishLanguage', 'languageResult', e.target.value)}
              error={!!errors['englishLanguage.languageResult']}
              helperText={errors['englishLanguage.languageResult']}
            />
          </Grid>
        )}
      </Grid>

      {/* 浮动错误提示 */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseErrorSnackbar} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {saveError || '表单验证失败，请检查输入信息'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PersonalInfoForm; 